import { Logger } from "logging";
import { useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { useDebounceCallback } from "usehooks-ts";

export interface LynePickerInputHandle {
  reset: () => void;
}

export interface LynePickerInputProps {
  logger: Logger;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  /**
   * When this value changes the input DOM value is imperatively updated and
   * any pending debounce is cancelled. Pass `""` to clear the input.
   */
  externalValue?: string;
  /** Called after the debounce delay with the current typed value. */
  onDebouncedChange: (value: string) => void;
  /**
   * Assign a ref here to get an imperative `reset()` handle.
   * Calling it clears the DOM input, cancels the debounce, and focuses.
   */
  resetRef: React.RefObject<LynePickerInputHandle | null>;
  debounceMs?: number;
}

/**
 * Uncontrolled autocomplete input with built-in debouncing and a guard that
 * suppresses spurious `input` events fired by the Lyne autocomplete during
 * option selection / focus cycles.
 */
export const LynePickerInput: React.FC<LynePickerInputProps> = ({
  logger,
  placeholder,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  externalValue,
  onDebouncedChange,
  resetRef,
  debounceMs = 200,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isResetting = useRef(false);

  // Keep a stable ref to the latest onDebouncedChange so the debounced
  // function itself never changes reference (avoids re-running the
  // externalValue effect on every render).
  const onDebouncedChangeRef = useRef(onDebouncedChange);
  useEffect(() => {
    onDebouncedChangeRef.current = onDebouncedChange;
  });

  const setValueDebounced = useDebounceCallback(
    useCallback(
      (value: string) => {
        logger.debug(`setValueDebounced(): '${value}'`);
        onDebouncedChangeRef.current(value);
      },
      [logger]
    ),
    debounceMs
  );

  const resetInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) {
      logger.debug(`resetInput(): ref is not set.`);
      return;
    }
    logger.debug(`resetInput(): '${el.value}' -> ''`);
    el.value = "";
    el.focus();
    el.select();
  }, [logger]);

  useImperativeHandle(
    resetRef,
    () => ({
      reset: () => {
        logger.debug(`reset(): isResetting ${isResetting.current} -> true`);
        isResetting.current = true;
        setValueDebounced.cancel();
        resetInput();

        // Re-clear after one event-loop tick: Lyne writes the selected option's
        // value back to the input synchronously after onOptionselected returns.
        setTimeout(() => {
          logger.debug(`reset(): isResetting ${isResetting.current} -> false`);
          resetInput();
          isResetting.current = false;
        }, 0);
      },
    }),
    [logger, setValueDebounced, resetInput]
  );

  // Sync externalValue → DOM input imperatively (avoids controlled-input
  // conflicts with Lyne's internal updates).
  useEffect(() => {
    if (externalValue === undefined) return;
    isResetting.current = true;
    setValueDebounced.cancel();

    const el = inputRef.current;
    logger.debug(`useEffect(externalValue): exists:=${el != null}, value:='${el?.value}' -> '${externalValue}'`);
    if (el) {
      el.value = externalValue;
    }

    // Re-enable after one event-loop tick — Lyne fires any synthetic input
    // events synchronously during the DOM write above.
    const t = setTimeout(() => {
      isResetting.current = false;
    }, 0);
    return () => clearTimeout(t);
  }, [logger, externalValue, setValueDebounced]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) {
      logger.debug(`useEffect(autoFocus): ref is not set.`);
      return;
    }

    logger.debug(`useEffect(autoFocus): ${autoFocus ? "auto-focusing" : "not auto-focusing"}`);
    if (autoFocus) el.focus();
  }, [autoFocus, logger]);

  return (
    <input
      ref={inputRef}
      disabled={disabled}
      readOnly={readOnly}
      autoComplete="off"
      placeholder={placeholder}
      defaultValue={externalValue ?? ""}
      onInput={() => {
        if (isResetting.current) return;
        const newValue = inputRef.current?.value ?? "";
        logger.debug(`onInput(): '${newValue}'`);
        setValueDebounced(newValue);
      }}
    />
  );
};
